import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { DirectusService } from '../core/services/directus.service';

type DirectusRecord = Record<string, unknown>;

interface ProjectRow {
  id: string;
  title: string;
  visibility: string;
  collectionsLabel: string;
  createdAt: Date | null;
  createdAtLabel: string;
  imageUrl: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="portfolio-page">
      <header class="portfolio-top">
        <div>
          <h1>Projects <span>{{ filteredRows.length }}</span></h1>
          <p>Create, edit and manage your portfolio projects.</p>
        </div>
        <div class="actions">
          <button type="button" class="btn icon-btn">...</button>
          <button type="button" class="btn btn-outline" (click)="openCollections()">Manage Collections</button>
          <button type="button" class="btn btn-primary" (click)="addProject()">+ Add Project</button>
        </div>
      </header>

      <section class="table-shell">
        <div class="table-tools">
          <select [(ngModel)]="visibilityFilter" (ngModelChange)="applyFilters()">
            <option value="all">Project Visibility</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select [(ngModel)]="collectionFilter" (ngModelChange)="applyFilters()">
            <option value="all">Select Collections</option>
            <option *ngFor="let collection of collectionOptions" [value]="collection">{{ collection }}</option>
          </select>
        </div>

        <p class="state" *ngIf="loading">Loading projects...</p>
        <p class="state error" *ngIf="error">{{ error }}</p>
        <p class="state" *ngIf="!loading && !error && filteredRows.length === 0">No projects found.</p>

        <div class="table-wrap" *ngIf="!loading && !error && filteredRows.length > 0">
          <table>
            <thead>
              <tr>
                <th class="check-col"><input type="checkbox" /></th>
                <th class="title-col">Title</th>
                <th>Collections</th>
                <th class="created-col">Created on</th>
                <th class="menu-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows">
                <td><input type="checkbox" /></td>
                <td class="title-cell">
                  <img [src]="row.imageUrl" alt="" />
                  <span>{{ row.title }}</span>
                </td>
                <td>{{ row.collectionsLabel }}</td>
                <td>{{ row.createdAtLabel }}</td>
                <td class="menu-cell">
                  <button type="button" class="edit-btn" (click)="editProject(row)">Edit</button>
                  <button type="button" class="menu-btn" (click)="toggleMenu(row.id, $event)">...</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div
        class="row-menu"
        [style.top.px]="menuTop"
        [style.left.px]="menuLeft"
        *ngIf="openMenuId"
        (click)="$event.stopPropagation()"
      >
        <button type="button" class="danger" (click)="deleteProjectById(openMenuId)">Delete</button>
      </div>
    </section>
  `,
  styles: [
    `
      .portfolio-page {
        display: grid;
        gap: 14px;
        width: 100%;
        max-width: var(--app-max-width);
        margin: 0 auto;
      }
      .portfolio-top {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
      }
      h1 {
        margin: 0;
        font-size: var(--type-page-title);
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.1;
      }
      h1 span {
        color: var(--app-muted);
        font-size: 0.9em;
      }
      .portfolio-top p {
        margin: 0.25rem 0 0;
        color: var(--app-muted);
        font-size: var(--type-body);
      }
      .actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .btn {
        border: 1px solid var(--app-border);
        background: var(--app-surface);
        color: var(--app-text);
        border-radius: 999px;
        padding: 0.45rem 0.92rem;
        font-weight: 700;
        cursor: pointer;
      }
      .btn-primary {
        background: var(--app-primary);
        border-color: var(--app-primary);
        color: #fff;
      }
      .btn-outline {
        color: var(--app-primary);
      }
      .icon-btn {
        width: 38px;
        height: 38px;
        padding: 0;
      }
      .table-shell {
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius);
        background: var(--app-surface);
        overflow: hidden;
      }
      .table-tools {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        padding: 1rem 1.1rem 0.85rem;
        border-bottom: 1px solid var(--app-border);
      }
      select {
        border: 1px solid var(--app-border);
        border-radius: 999px;
        background: var(--app-surface-soft);
        color: var(--app-muted);
        padding: 0.4rem 0.8rem;
        min-width: 190px;
      }
      .state {
        margin: 0;
        padding: 0.95rem 1.1rem;
        color: var(--app-muted);
      }
      .state.error {
        color: #b91c1c;
      }
      .table-wrap {
        overflow: auto;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th {
        text-align: left;
        background: var(--app-primary-soft);
        color: var(--app-text);
        padding: 0.7rem 0.85rem;
        font-size: var(--type-body);
        font-weight: 600;
      }
      td {
        padding: 0.85rem;
        border-top: 1px solid var(--app-border);
        color: var(--app-text);
        font-size: var(--type-body);
        vertical-align: middle;
      }
      .check-col {
        width: 40px;
      }
      .title-col {
        min-width: 320px;
      }
      .created-col {
        min-width: 180px;
      }
      .menu-col {
        width: 80px;
      }
      .title-cell {
        display: flex;
        align-items: center;
        gap: 0.85rem;
      }
      .title-cell img {
        width: 100px;
        height: 64px;
        object-fit: cover;
        border-radius: 8px;
        border: 1px solid var(--app-border);
      }
      .menu-cell {
        text-align: right;
        white-space: nowrap;
      }
      .edit-btn {
        opacity: 0;
        visibility: hidden;
        border: 1px solid var(--app-border);
        background: #fff;
        color: var(--app-primary);
        border-radius: 999px;
        padding: 0.34rem 0.74rem;
        font-weight: 700;
        margin-right: 0.35rem;
        cursor: pointer;
      }
      tr:hover .edit-btn,
      tr:focus-within .edit-btn {
        opacity: 1;
        visibility: visible;
      }
      .menu-btn {
        width: 34px;
        height: 34px;
        border-radius: 999px;
        border: 1px solid var(--app-border);
        color: var(--app-primary);
        background: #fff;
        cursor: pointer;
      }
      .row-menu {
        position: fixed;
        z-index: 40;
        min-width: 130px;
        border: 1px solid var(--app-border);
        border-radius: 10px;
        background: #fff;
        box-shadow: var(--app-shadow);
        padding: 0.22rem;
      }
      .row-menu button {
        width: 100%;
        text-align: left;
        border: 0;
        background: transparent;
        padding: 0.45rem 0.55rem;
        border-radius: 8px;
        cursor: pointer;
      }
      .row-menu button:hover {
        background: var(--app-surface-soft);
      }
      .row-menu .danger {
        color: #b91c1c;
      }
    `
  ]
})
export class PortfolioProjectsComponent implements OnInit {
  rows: ProjectRow[] = [];
  filteredRows: ProjectRow[] = [];
  collectionOptions: string[] = [];
  loading = false;
  error = '';
  visibilityFilter: 'all' | 'published' | 'draft' | 'archived' = 'all';
  collectionFilter = 'all';
  openMenuId: string | null = null;
  menuTop = 0;
  menuLeft = 0;

  constructor(
    private readonly directus: DirectusService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    void this.loadProjects();
  }

  @HostListener('document:click')
  closeMenusOnOutside(): void {
    this.openMenuId = null;
  }

  openCollections(): void {
    void this.router.navigateByUrl('/projects/collections');
  }

  addProject(): void {
    window.open(`${environment.directusUrl}/admin/content/projects/+`, '_blank', 'noopener,noreferrer');
  }

  editProject(row: ProjectRow): void {
    window.open(`${environment.directusUrl}/admin/content/projects/${encodeURIComponent(row.id)}`, '_blank', 'noopener,noreferrer');
  }

  toggleMenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.openMenuId === id) {
      this.openMenuId = null;
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.menuLeft = Math.max(12, rect.right - 128);
    this.menuTop = rect.bottom + 8;
    this.openMenuId = id;
  }

  deleteProjectById(id: string): void {
    const row = this.rows.find((r) => r.id === id);
    if (!row) return;
    const confirmed = window.confirm(`Delete project "${row.title}"?`);
    if (!confirmed) return;
    void this.deleteProject(id);
  }

  async deleteProject(id: string): Promise<void> {
    try {
      await this.directus.remove('projects', id);
      this.rows = this.rows.filter((row) => row.id !== id);
      this.openMenuId = null;
      this.applyFilters();
    } catch {
      this.error = 'Failed to delete project.';
    }
  }

  applyFilters(): void {
    let next = [...this.rows];
    if (this.visibilityFilter !== 'all') {
      next = next.filter((row) => row.visibility === this.visibilityFilter);
    }
    if (this.collectionFilter !== 'all') {
      next = next.filter((row) => row.collectionsLabel === this.collectionFilter);
    }
    this.filteredRows = next;
  }

  private async loadProjects(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const records = await this.fetchProjectsSafe();
      this.rows = records.map((record) => this.mapProjectRow(record));
      this.collectionOptions = [...new Set(this.rows.map((row) => row.collectionsLabel).filter((label) => label !== 'Uncategorized'))]
        .sort((a, b) => a.localeCompare(b));
      this.applyFilters();
    } catch {
      this.error = 'Failed to load projects.';
      this.rows = [];
      this.filteredRows = [];
      this.collectionOptions = [];
    } finally {
      this.loading = false;
    }
  }

  private async fetchProjectsSafe(): Promise<DirectusRecord[]> {
    try {
      return await this.directus.list('projects', {
        limit: 300,
        sort: '-sort',
        fields: 'id,title,status,cover_image,start_date,category,slug'
      });
    } catch {
      return this.directus.list('projects', { limit: 300 });
    }
  }

  private mapProjectRow(record: DirectusRecord): ProjectRow {
    const id = String(record['id'] ?? '');
    const title = this.stringValue(record['title']) || this.stringValue(record['name']) || 'Untitled project';
    const visibilityRaw = (this.stringValue(record['visibility']) || this.stringValue(record['status']) || 'draft').toLowerCase();
    const visibility = visibilityRaw.includes('publish')
      ? 'published'
      : visibilityRaw.includes('archive')
      ? 'archived'
      : 'draft';

    const collectionsLabel = this.readCollections(record);
    const createdAtValue =
      this.stringValue(record['date_created']) ||
      this.stringValue(record['created_at']) ||
      this.stringValue(record['start_date']);
    const createdAt = createdAtValue ? new Date(createdAtValue) : null;
    const createdAtLabel = createdAt
      ? createdAt.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';

    return {
      id,
      title,
      visibility,
      collectionsLabel,
      createdAt,
      createdAtLabel,
      imageUrl: this.resolveProjectImage(record)
    };
  }

  private stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private resolveProjectImage(record: DirectusRecord): string {
    const candidate =
      this.readFileId(record['image']) ||
      this.readFileId(record['featured_image']) ||
      this.readFileId(record['cover_image']) ||
      this.readFileId(record['seo_image']) ||
      this.readFileId(record['thumbnail']) ||
      '';

    if (candidate) {
      return `${environment.directusUrl}/assets/${encodeURIComponent(candidate)}`;
    }
    return 'https://placehold.co/200x120/e6f4fb/57718f?text=Project';
  }

  private readFileId(value: unknown): string {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (value && typeof value === 'object') {
      const id = (value as DirectusRecord)['id'];
      if (typeof id === 'string' || typeof id === 'number') return String(id);
    }
    return '';
  }

  private readCollections(record: DirectusRecord): string {
    const candidates = ['collections', 'collection', 'category', 'project_collection'];
    for (const key of candidates) {
      const raw = record[key];
      const parsed = this.collectionValue(raw);
      if (parsed) return parsed;
    }
    return 'Uncategorized';
  }

  private collectionValue(value: unknown): string {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      const names = value
        .map((item) => this.collectionValue(item))
        .filter((name) => name.length > 0);
      return names.join(', ');
    }
    if (typeof value === 'object') {
      const record = value as DirectusRecord;
      return (
        this.stringValue(record['name']) ||
        this.stringValue(record['title']) ||
        this.stringValue(record['label']) ||
        this.stringValue(record['category']) ||
        this.stringValue(record['id'])
      );
    }
    return '';
  }
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="collections-page">
      <header class="collections-top">
        <div>
          <h1>Collections</h1>
          <p>Group portfolio projects by focus area.</p>
        </div>
        <div class="actions">
          <button type="button" class="btn btn-outline" (click)="openProjects()">Back to Projects</button>
        </div>
      </header>

      <section class="list-shell">
        <p class="state" *ngIf="loading">Loading collections...</p>
        <p class="state error" *ngIf="error">{{ error }}</p>
        <p class="state" *ngIf="!loading && !error && rows.length === 0">No collections found.</p>

        <div class="list-row" *ngFor="let row of rows">
          <div class="name">{{ row.name }}</div>
          <div class="meta">Connected projects: {{ row.count }}</div>
        </div>
      </section>
    </section>
  `,
  styles: [
    `
      .collections-page {
        display: grid;
        gap: 14px;
        width: 100%;
        max-width: var(--app-max-width);
        margin: 0 auto;
      }
      .collections-top {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 10px;
      }
      h1 {
        margin: 0;
        font-size: var(--type-page-title);
        font-weight: 700;
      }
      p {
        margin: 0.3rem 0 0;
        color: var(--app-muted);
        font-size: var(--type-body);
      }
      .btn {
        border: 1px solid var(--app-border);
        background: #fff;
        color: var(--app-primary);
        border-radius: 999px;
        padding: 0.45rem 0.9rem;
        font-weight: 700;
        cursor: pointer;
      }
      .list-shell {
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius);
        background: #fff;
        overflow: hidden;
      }
      .list-row {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 0.8rem;
        align-items: center;
        padding: 1rem 1.2rem;
        border-top: 1px solid var(--app-border);
      }
      .list-row:first-of-type {
        border-top: 0;
      }
      .name {
        font-size: var(--type-subsection-title);
        font-weight: 700;
      }
      .meta {
        color: var(--app-muted);
        font-size: var(--type-body);
      }
      .state {
        margin: 0;
        padding: 1rem 1.2rem;
        color: var(--app-muted);
      }
      .state.error {
        color: #b91c1c;
      }
    `
  ]
})
export class PortfolioCollectionsComponent implements OnInit {
  rows: Array<{ name: string; count: number }> = [];
  loading = false;
  error = '';

  constructor(
    private readonly directus: DirectusService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    void this.loadCollections();
  }

  openProjects(): void {
    void this.router.navigateByUrl('/projects/projects');
  }

  private async loadCollections(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const projects = await this.directus.list('projects', { limit: 300 });
      const counts = new Map<string, number>();

      for (const project of projects) {
        const names = this.readCollectionNames(project);
        if (names.length === 0) {
          counts.set('Uncategorized', (counts.get('Uncategorized') || 0) + 1);
        } else {
          for (const name of names) {
            counts.set(name, (counts.get(name) || 0) + 1);
          }
        }
      }

      this.rows = [...counts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      this.error = 'Failed to load collections.';
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  private readCollectionNames(record: DirectusRecord): string[] {
    const raw =
      record['collections'] ??
      record['collection'] ??
      record['category'] ??
      record['project_collection'];

    return this.flattenCollectionNames(raw);
  }

  private flattenCollectionNames(value: unknown): string[] {
    if (!value) return [];
    if (typeof value === 'string' || typeof value === 'number') return [String(value)];
    if (Array.isArray(value)) return value.flatMap((item) => this.flattenCollectionNames(item));
    if (typeof value === 'object') {
      const record = value as DirectusRecord;
      const name =
        this.stringValue(record['name']) ||
        this.stringValue(record['title']) ||
        this.stringValue(record['label']) ||
        this.stringValue(record['category']);
      return name ? [name] : [];
    }
    return [];
  }

  private stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
